import {EmployeeRef, HibobOffice, HibobResidence, WorkSite} from "./Model";
import {create} from "zustand";
import {derivedRemoteStore} from "./DerivedStore";
import {useWorkSiteStore} from "./WorkSiteStore";
import {EmployeeMap, useEmployeeMapStore} from "./EmployeeMapStore";
import {useHibobOfficeStore} from "./HibobOfficeStore";
import {associateBy, groupBy} from "../util/MapUtil";
import {Opt} from "../util/Optional";

const unknownWorkSite: WorkSite = {id: -1, name: 'Unknown', archived: false};

export class WorkSites {
    private readonly workSites: Map<WorkSite['id'], WorkSite>;
    private readonly hibobOffices: Map<HibobOffice['id'], HibobOffice>;
    readonly hibobOfficesByWorkSiteId: Map<WorkSite['id'] | null, HibobOffice[]>;

    constructor(
        workSites: WorkSite[],
        hibobOffices: HibobOffice[],
        private readonly employeeMap: EmployeeMap,
    ) {
        this.workSites = associateBy(workSites, site => site.id);
        this.hibobOffices = associateBy(hibobOffices, office => office.id);
        this.hibobOfficesByWorkSiteId = groupBy(hibobOffices, office => office.workSiteId);
    }

    residencyById(workSiteId: Opt<WorkSite['id']>, hibobOfficeId: Opt<HibobOffice['id']>): HibobResidence & {workSite: WorkSite} {
        return {
            workSite: (workSiteId != null && this.workSites.get(workSiteId)) || unknownWorkSite,
            hibobOffice: hibobOfficeId != null && this.hibobOffices.get(hibobOfficeId) || null,
        };
    }

    residenceByEmployee(employeeRef: EmployeeRef): HibobResidence & {workSite: WorkSite} {
        const employee = this.employeeMap.get(employeeRef);
        return this.residencyById(employee?.siteId, employee?.hibobOfficeId);
    }

    nestedOffices(workSiteId: WorkSite['id']): HibobOffice[] {
        return this.hibobOfficesByWorkSiteId.get(workSiteId) ?? [];
    }

    static EMPTY = new WorkSites([], [], EmployeeMap.EMPTY);
}

export function badgeOrDefault(workSiteOrHibobOffice: {badge?: string, name: string}): string {
    return workSiteOrHibobOffice.badge ?? workSiteOrHibobOffice.name.split(' ')[0].toLowerCase();
}

export const useWorkSiteMapStore = create(
    derivedRemoteStore(
        {workSites: useWorkSiteStore, hibobOffices: useHibobOfficeStore, employeeMap: useEmployeeMapStore},
        ({workSites, hibobOffices, employeeMap}) => new WorkSites(workSites, hibobOffices, employeeMap),
    )
);
